import { useCallback, useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useMutation } from '@tanstack/react-query';
import { usePatientCard } from '../../../../hooks/usePatientCard';
import {
    createDmPatient,
    linkExistingDmPatient,
    type CreateDmPatientBody,
    type DmPatient
} from '../../../../services/dmPatient';
import { DmShellLayout } from './DmShellLayout';
import { SuccessBanner, ErrorBanner } from './DmBanners';
import { SkeletonCard } from './DmSkeletons';
import {
    DmField,
    dmInputClasses,
    dmInputErrorClasses,
    DmBtnPrimary,
    DmBtnSecondary
} from './DmFormPrimitives';
import type { ActionRunnerProps } from '../types';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DOB_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The patient card may store DOB as either "YYYY-MM-DD" or an ISO-8601
 * datetime ("1990-01-15T00:00:00.000Z"). The HTML date input only renders a
 * value if it matches "YYYY-MM-DD" exactly, so we extract the date prefix.
 */
function normalizeDob(raw: string | null | undefined): string {
    if (!raw) return '';
    const match = /^(\d{4}-\d{2}-\d{2})/.exec(raw);
    return match ? match[1] : '';
}

interface DuplicatePatient {
    id: number;
    firstName: string | null;
    lastName: string | null;
    email: string | null;
}

interface FormValues {
    firstName: string;
    lastName: string;
    email: string;
    dateOfBirth: string;
    phone: string;
}

interface FormErrors {
    firstName?: string;
    lastName?: string;
    email?: string;
    dateOfBirth?: string;
}

function validateForm(values: FormValues): FormErrors {
    const errors: FormErrors = {};
    if (!values.firstName.trim()) errors.firstName = 'First name is required.';
    if (!values.lastName.trim()) errors.lastName = 'Last name is required.';
    if (!values.email.trim()) errors.email = 'Email is required.';
    else if (!EMAIL_REGEX.test(values.email.trim())) errors.email = 'Enter a valid email address.';
    if (values.dateOfBirth && !DOB_REGEX.test(values.dateOfBirth)) {
        errors.dateOfBirth = 'Date must be in YYYY-MM-DD format.';
    }
    return errors;
}

interface CreateDmPatientLandingProps extends ActionRunnerProps {
    inWorkflow?: boolean;
    onCompleted?: () => void;
}

function CreateDmPatientLanding({
    patientId,
    onBack,
    inWorkflow = false,
    onCompleted
}: CreateDmPatientLandingProps) {
    const { data: card, isLoading: cardLoading, refetch: refetchCard } = usePatientCard(patientId);

    const [values, setValues] = useState<FormValues>({
        firstName: '',
        lastName: '',
        email: '',
        dateOfBirth: '',
        phone: ''
    });
    const prefilled = useRef(false);

    const [touched, setTouched] = useState<Partial<Record<keyof FormValues, boolean>>>({});
    const [conflict, setConflict] = useState<DuplicatePatient | null>(null);
    const [createdPatient, setCreatedPatient] = useState<DmPatient | null>(null);
    const [linked, setLinked] = useState<DmPatient | null>(null);

    useEffect(() => {
        if (card && !prefilled.current) {
            prefilled.current = true;
            setValues({
                firstName: card.firstName ?? '',
                lastName: card.lastName ?? '',
                email: card.email ?? '',
                dateOfBirth: normalizeDob(card.dateOfBirth),
                phone: card.phone ?? ''
            });
        }
    }, [card]);

    const handleChange = useCallback(
        <K extends keyof FormValues>(field: K, value: FormValues[K]) => {
            setValues((prev) => ({ ...prev, [field]: value }));
        },
        []
    );

    const createMutation = useMutation({
        mutationFn: (body: CreateDmPatientBody) => createDmPatient(patientId, body),
        onSuccess: async (result) => {
            if (result.success) {
                await refetchCard();
                setCreatedPatient(result.dmPatient);
                onCompleted?.();
            } else if (result.isDuplicate) {
                setConflict(result.dmPatient);
            }
        }
    });

    const linkMutation = useMutation({
        mutationFn: () => linkExistingDmPatient(patientId, values.email.trim()),
        onSuccess: async (result) => {
            if (result.success) {
                await refetchCard();
                setLinked(result.dmPatient);
                onCompleted?.();
            }
        }
    });

    const formErrors = validateForm(values);
    const hasFormErrors = Object.keys(formErrors).length > 0;
    const isPending = createMutation.isPending || linkMutation.isPending;
    const canSubmit = !hasFormErrors && !isPending;

    const createErrorMessage =
        createMutation.data && !createMutation.data.success && !createMutation.data.isDuplicate
            ? createMutation.data.message
            : createMutation.error instanceof Error
              ? createMutation.error.message
              : null;

    const linkErrorMessage =
        linkMutation.data && !linkMutation.data.success
            ? linkMutation.data.message
            : linkMutation.error instanceof Error
              ? linkMutation.error.message
              : null;

    const submitError = createErrorMessage ?? linkErrorMessage;

    const handleSubmit = useCallback(() => {
        setTouched({ firstName: true, lastName: true, email: true, dateOfBirth: true });
        if (hasFormErrors) return;

        const body: CreateDmPatientBody = {
            firstName: values.firstName.trim(),
            lastName: values.lastName.trim(),
            email: values.email.trim()
        };
        if (values.dateOfBirth) body.dateOfBirth = values.dateOfBirth;
        if (values.phone.trim()) body.phone = values.phone.trim();

        createMutation.reset();
        createMutation.mutate(body);
    }, [values, hasFormErrors, createMutation]);

    const handleLinkInstead = useCallback(() => {
        linkMutation.reset();
        linkMutation.mutate();
    }, [linkMutation]);

    const handleRetry = useCallback(() => {
        createMutation.reset();
        linkMutation.reset();
    }, [createMutation, linkMutation]);

    const content = (
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
            {cardLoading && <SkeletonCard />}

            {/*  Success: linked to existing DM patient  */}
            {!cardLoading && linked && (
                <>
                    <SuccessBanner message={`Linked to existing DM patient (DM #${linked.id}).`} />
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Linked DM patient
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                            {[linked.firstName, linked.lastName].filter(Boolean).join(' ') || '—'}
                        </p>
                        <p className="text-sm text-gray-500">{linked.email ?? '—'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                            DM ID: <span className="font-mono">{linked.id}</span>
                        </p>
                    </div>
                    {!inWorkflow && (
                        <div className="flex justify-end pt-2">
                            <DmBtnSecondary onClick={onBack}>Back to actions</DmBtnSecondary>
                        </div>
                    )}
                </>
            )}

            {/*  Success: new DM patient created  */}
            {!cardLoading && createdPatient && !linked && (
                <>
                    <SuccessBanner message={`DM patient created. DM ID: ${createdPatient.id}`} />
                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            New DM ID
                        </p>
                        <p className="text-2xl font-bold text-gray-800 font-mono">
                            {createdPatient.id}
                        </p>
                    </div>
                    <p className="text-sm text-gray-500 text-center">
                        Next step: send the patient an activation link so they can access the DM
                        app.
                    </p>
                    {!inWorkflow && (
                        <div className="flex justify-end pt-2">
                            <DmBtnSecondary onClick={onBack}>Back to actions</DmBtnSecondary>
                        </div>
                    )}
                </>
            )}

            {/*  Conflict: email already in DM  */}
            {!cardLoading && conflict && !createdPatient && !linked && (
                <>
                    <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                        <ExclamationTriangleIcon className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-semibold text-yellow-900">
                                Email already in DM
                            </p>
                            <p className="text-xs text-yellow-700 mt-1">
                                A DM patient with this email exists. Link to them instead?
                            </p>
                        </div>
                    </div>
                    <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
                        <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">
                            Existing DM patient
                        </p>
                        <p className="text-sm font-semibold text-gray-900">
                            {[conflict.firstName, conflict.lastName].filter(Boolean).join(' ') ||
                                '—'}
                        </p>
                        <p className="text-sm text-gray-500">{conflict.email ?? '—'}</p>
                        <p className="text-xs text-gray-400 mt-1">
                            DM ID: <span className="font-mono">{conflict.id}</span>
                        </p>
                    </div>
                    {submitError && <ErrorBanner message={submitError} onRetry={handleRetry} />}
                    <div className="flex justify-end gap-2 pt-2">
                        {!inWorkflow && (
                            <DmBtnSecondary onClick={onBack} disabled={isPending}>
                                Cancel
                            </DmBtnSecondary>
                        )}
                        <DmBtnPrimary onClick={handleLinkInstead} disabled={isPending}>
                            {linkMutation.isPending ? 'Linking…' : 'Link existing instead'}
                        </DmBtnPrimary>
                    </div>
                </>
            )}

            {/*  Form: default state  */}
            {!cardLoading && !createdPatient && !conflict && !linked && (
                <>
                    {submitError && <ErrorBanner message={submitError} onRetry={handleRetry} />}

                    <div className="bg-white rounded-xl border border-gray-200 p-6">
                        <div className="flex flex-col gap-4">
                            <div className="grid grid-cols-2 gap-3">
                                <DmField
                                    label="First name"
                                    required
                                    error={touched.firstName ? formErrors.firstName : undefined}
                                >
                                    <input
                                        type="text"
                                        className={
                                            touched.firstName && formErrors.firstName
                                                ? dmInputErrorClasses
                                                : dmInputClasses
                                        }
                                        placeholder="Jane"
                                        value={values.firstName}
                                        onChange={(e) => handleChange('firstName', e.target.value)}
                                        onBlur={() =>
                                            setTouched((prev) => ({ ...prev, firstName: true }))
                                        }
                                    />
                                </DmField>
                                <DmField
                                    label="Last name"
                                    required
                                    error={touched.lastName ? formErrors.lastName : undefined}
                                >
                                    <input
                                        type="text"
                                        className={
                                            touched.lastName && formErrors.lastName
                                                ? dmInputErrorClasses
                                                : dmInputClasses
                                        }
                                        placeholder="Doe"
                                        value={values.lastName}
                                        onChange={(e) => handleChange('lastName', e.target.value)}
                                        onBlur={() =>
                                            setTouched((prev) => ({ ...prev, lastName: true }))
                                        }
                                    />
                                </DmField>
                            </div>

                            <DmField
                                label="Email"
                                required
                                error={touched.email ? formErrors.email : undefined}
                            >
                                <input
                                    type="email"
                                    className={
                                        touched.email && formErrors.email
                                            ? dmInputErrorClasses
                                            : dmInputClasses
                                    }
                                    placeholder="jane@example.com"
                                    value={values.email}
                                    onChange={(e) => handleChange('email', e.target.value)}
                                    onBlur={() => setTouched((prev) => ({ ...prev, email: true }))}
                                />
                            </DmField>

                            <div className="grid grid-cols-2 gap-3">
                                <DmField
                                    label="Date of birth"
                                    hint="Defaults to 1900-01-01"
                                    error={touched.dateOfBirth ? formErrors.dateOfBirth : undefined}
                                >
                                    <input
                                        type="date"
                                        className={
                                            touched.dateOfBirth && formErrors.dateOfBirth
                                                ? dmInputErrorClasses
                                                : dmInputClasses
                                        }
                                        value={values.dateOfBirth}
                                        onChange={(e) =>
                                            handleChange('dateOfBirth', e.target.value)
                                        }
                                        onBlur={() =>
                                            setTouched((prev) => ({ ...prev, dateOfBirth: true }))
                                        }
                                    />
                                </DmField>
                                <DmField label="Phone" hint="Normalized to E.164">
                                    <input
                                        type="tel"
                                        className={dmInputClasses}
                                        placeholder="+1 555 000 1234"
                                        value={values.phone}
                                        onChange={(e) => handleChange('phone', e.target.value)}
                                    />
                                </DmField>
                            </div>
                        </div>
                    </div>

                    <p className="text-xs text-gray-400">
                        Practice must have{' '}
                        <code className="bg-gray-100 px-1 rounded text-[11px] text-gray-600">
                            dmDoctorId
                        </code>{' '}
                        +{' '}
                        <code className="bg-gray-100 px-1 rounded text-[11px] text-gray-600">
                            dmPracticeId
                        </code>{' '}
                        configured.
                    </p>

                    <div className="flex justify-end gap-2 pt-2">
                        {!inWorkflow && (
                            <DmBtnSecondary onClick={onBack} disabled={isPending}>
                                Cancel
                            </DmBtnSecondary>
                        )}
                        <DmBtnPrimary onClick={handleSubmit} disabled={!canSubmit}>
                            {createMutation.isPending ? 'Creating…' : 'Create patient'}
                        </DmBtnPrimary>
                    </div>
                </>
            )}
        </div>
    );

    if (inWorkflow) return content;

    return (
        <DmShellLayout title="Create DM patient" onBack={onBack}>
            {content}
        </DmShellLayout>
    );
}

export { CreateDmPatientLanding };
